import React, { useEffect, useState } from 'react';
import axios from 'axios';
import BASE_URL from '../../../config/config';
import { jwtDecode } from 'jwt-decode';
import Popup from '../../../components/Popup';
import { FiInbox, FiClock, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';

const formatDept = (slug) => {
  if (!slug) return 'Sin asignar';
  const map = {
    desarrollo: 'Dpto. Desarrollo',
    diseno: 'Dpto. Diseño',
    soporte: 'Dpto. Soporte',
    infraestructura: 'Dpto. Infraestructura',
    ventas: 'Dpto. Ventas',
    administracion: 'Dpto. Administración',
    no_asignado: 'Sin asignar',
  };
  if (map[slug]) return map[slug];
  const words = String(slug).replace(/[_-]+/g, ' ').split(' ').filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  return `Dpto. ${words.join(' ')}`;
};

/**
 * Estadísticas generales de tickets, visibles para técnicos y administradores.
 * El desglose por departamento (sólo admin) se muestra en un popup aparte.
 */
const DashboardStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('AuthToken');
    if (token) {
      try {
        setUserRole(jwtDecode(token).role || '');
      } catch (err) {
        console.warn('Token decode failed', err);
      }
    }

    const fetchStats = async () => {
      setLoading(true);
      try {
        const resp = await axios.get(`${BASE_URL}/api/admin/stats/tickets`, {
          headers: { Authorization: token },
        });
        setStats(resp.data);
      } catch (err) {
        console.error('Error obteniendo estadísticas:', err);
        setError('No se pudieron cargar las estadísticas');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <div className="dash-stats-grid dash-stats-status">Cargando estadísticas...</div>;
  }

  if (error || !stats) {
    return <div className="dash-stats-grid dash-stats-status">{error || 'Sin datos'}</div>;
  }

  const deptEntries = stats.avgResolutionByDept ? Object.entries(stats.avgResolutionByDept) : [];

  return (
    <div className="dash-stats-wrap">
      <div className="dash-stats-grid">
        <div className="dash-stat-card">
          <div className="dash-stat-icon dash-stat-icon-azul"><FiInbox /></div>
          <div className="dash-stat-body">
            <span className="dash-stat-label">Tickets abiertos</span>
            <span className="dash-stat-value">{stats.openTotal}</span>
            <span className="dash-stat-sub">
              Hoy <b>{stats.openedToday >= 0 ? `+${stats.openedToday}` : stats.openedToday}</b>
            </span>
          </div>
        </div>

        <div className="dash-stat-card">
          <div className="dash-stat-icon dash-stat-icon-verde"><FiCheckCircle /></div>
          <div className="dash-stat-body">
            <span className="dash-stat-label">Resueltos hoy</span>
            <span className="dash-stat-value">{stats.resolvedToday}</span>
          </div>
        </div>

        <div className="dash-stat-card">
          <div className="dash-stat-icon dash-stat-icon-rojo"><FiAlertTriangle /></div>
          <div className="dash-stat-body">
            <span className="dash-stat-label">Tickets urgentes</span>
            <span className="dash-stat-value">{stats.urgentOpenTotal}</span>
          </div>
        </div>

        <div className="dash-stat-card">
          <div className="dash-stat-icon dash-stat-icon-gris"><FiClock /></div>
          <div className="dash-stat-body">
            <span className="dash-stat-label">Tiempo medio de resolución</span>
            <span className="dash-stat-value dash-stat-value-sm">{stats.avgResolutionTimeHuman || '—'}</span>
            {userRole === 'admin' && (
              <button className="dash-stat-breakdown-btn" onClick={() => setPopupOpen(true)}>
                Ver por departamento
              </button>
            )}
          </div>
        </div>
      </div>

      <Popup open={popupOpen} title="Desglose por departamento" onClose={() => setPopupOpen(false)} cancelText="Cerrar">
        {deptEntries.length > 0 ? (
          <div className="dash-desglose">
            {deptEntries.map(([dept, data]) => {
              const isInterv = dept.startsWith('intervencion_');
              const displayName = isInterv
                ? `Intervención en ${formatDept(dept.replace('intervencion_', '') || 'no_asignado')}`
                : formatDept(dept);

              return (
                <div key={dept} className="dash-desglose-fila">
                  <div>
                    <div className="dash-desglose-nombre">{displayName}</div>
                    <div className="dash-desglose-conteo">Tickets: {data.count}</div>
                  </div>
                  <div className="dash-desglose-tiempo">{data.avgResolutionTimeHuman}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div>No hay datos por departamento.</div>
        )}
      </Popup>
    </div>
  );
};

export default DashboardStats;
